import React from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { AutoFormFieldProps } from "@autoform/react";
import { GetListParams, useDataProvider } from "@refinedev/core";
import { AlertCircleIcon } from "lucide-react";

import { RemoteSelect } from "@/components/fields/remote-select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

export type IRemoteSelectFieldCustomData = {
  getListParams: (query: string) => GetListParams;
  accessorFn: (option: any) => {
    id: string;
    name: string;
    iconUrl?: string | undefined;
    number?: string | undefined;
  };
  dataProviderName?: string;
};

export const RemoteSelectField: React.FC<AutoFormFieldProps> = ({
  id,
  label,
  field,
  inputProps,
}) => {
  const form = useFormContext();
  const value = form.watch(id);
  const customData = field.fieldConfig?.customData as
    | IRemoteSelectFieldCustomData
    | undefined;
  const { getList } = useDataProvider()(customData?.dataProviderName);
  if (!field.fieldConfig?.customData) {
    return (
      <Alert variant="destructive" className="max-w-md">
        <AlertCircleIcon />
        <AlertTitle>Oops</AlertTitle>
        <AlertDescription>
          Add fetch function in customData in order to parse remoteSelectField
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <RemoteSelect
      value={value}
      triggerType="change"
      onValueChanged={(value) => {
        form.setValue(id, value);
        inputProps.onChange({
          target: {
            name: id,
            value: value,
          },
        });
      }}
      accessorFn={customData!.accessorFn}
      onSearch={async (query, onStatusChanged) => {
        if (!query) {
          return [];
        }

        onStatusChanged("loading");

        const response = await getList?.(customData!.getListParams(query));

        onStatusChanged("success");

        if (!response?.data) {
          return [];
        }

        return response?.data.map((d) => d.document);
      }}
    />
  );
};
